import type { Entity, Persisted } from '@tunasong/schemas'

/**
 * Eviction bookkeeping for the entity cache. GH #1643
 *
 * Cached songs carry their whole base64 Y.Doc, and nothing evicted them, so the cache grew by every song visited
 * in a session. An entry is now evictable once nobody references it and it has not been touched for a grace
 * period. This state lives outside the Zustand store on purpose: retaining an entry must not re-render readers.
 */

/** The count cap the store evicts down to */
export const maxCachedEntries = 200

/** How long an unreferenced entry stays, to absorb StrictMode double mounts and quick back-and-forth navigation */
export const evictionGraceMs = 30_000

/** Mounted readers per id. An entry is pinned while its own id or its parentId is retained. */
const refCounts = new Map<string, number>()

/** id -> last touch time. Map order is the eviction order: least recently touched first. */
const lastTouched = new Map<string, number>()

export const touch = (id: string, now = Date.now()) => {
  lastTouched.delete(id)
  lastTouched.set(id, now)
}

export const forget = (id: string) => {
  lastTouched.delete(id)
}

export const forgetAll = () => {
  lastTouched.clear()
}

export const retainRef = (id: string) => {
  refCounts.set(id, (refCounts.get(id) ?? 0) + 1)
}

/** Returns true when the last reference was dropped */
export const releaseRef = (id: string) => {
  const count = refCounts.get(id) ?? 0
  if (count <= 1) {
    refCounts.delete(id)
    return count === 1
  }
  refCounts.set(id, count - 1)
  return false
}

const isRetained = (id?: string | null) => Boolean(id && refCounts.has(id))

interface EvictionSource {
  entityCache: ReadonlyMap<string, Persisted<Entity>>
  pendingIds: ReadonlySet<string>
  unconfirmed: ReadonlyMap<string, unknown>
}

/**
 * The entries to evict to bring the cache down to `maxEntries`, least recently touched first. Retained entries,
 * children of a retained parent, and entries carrying local changes are never chosen. `retryAt` is when the first
 * entry still inside its grace period leaves it, if the cache would remain over `maxEntries` until then.
 *
 * TODO(GH #1643): weight by `yDoc.length` rather than counting entries; a handful of large songs outweighs
 * hundreds of folders.
 */
export const selectEvictable = (source: EvictionSource, maxEntries: number, now = Date.now()) => {
  const { entityCache, pendingIds, unconfirmed } = source
  let excess = entityCache.size - maxEntries
  const ids: string[] = []
  let retryAt: number | undefined

  for (const [id, touchedAt] of lastTouched) {
    if (excess <= 0) {
      break
    }
    if (now - touchedAt < evictionGraceMs) {
      // Touch order is time order, so every later entry is inside its grace period too
      retryAt = touchedAt + evictionGraceMs
      break
    }
    const entry = entityCache.get(id)
    if (!entry || isRetained(id) || isRetained(entry.parentId) || pendingIds.has(id) || unconfirmed.has(id)) {
      continue
    }
    ids.push(id)
    excess -= 1
  }
  return { ids, retryAt }
}
